import { formatThousandsComma } from '@noor/lib';

/**
 * Abbreviation i18n keys by magnitude, index 0 = thousand. Mirrors the old
 * `getShorterPriceNumber`'s `abbrev` array (keys under `get_shorter_number.*`).
 */
const ABBREV = [
  'get_shorter_number.thousand',
  'get_shorter_number.million',
  'get_shorter_number.billion',
  'get_shorter_number.trillion',
  'get_shorter_number.quadrillion',
  'get_shorter_number.quintillion',
  'get_shorter_number.sextillion',
  'get_shorter_number.septillion',
  'get_shorter_number.octillion',
  'get_shorter_number.nonillion',
  'get_shorter_number.decillion',
] as const;

const MAX_DECIMALS = 3;

/**
 * Abbreviate a money amount the way the old dashboard did — `-166608940`
 * becomes `-166,609 M` (with `t('get_shorter_number.million')` supplying the
 * "M"). A 1-to-1 port of `utils/getShorterPriceNumber.js`, including its
 * quirks, because the balance tile is compared against the live panel:
 *
 * - The sign is stripped up front and re-applied at the end, so rounding
 *   happens on the magnitude (`-1999` → `-2 K`, not `-1 K`).
 * - Decimals are chosen from the *unrounded* quotient: up to three places,
 *   with trailing zeros trimmed — so `1.5 M` keeps one place and `1.25 M`
 *   keeps two, rather than every value padding to a fixed width.
 * - Rounding that carries to exactly 1000 promotes to the next unit
 *   (`999_999_999` → `1 B`, never `1000 M`).
 * - The decimal separator is a COMMA, matching the original's `replace('.', ',')`.
 * - Amounts below the smallest unit fall through to plain comma grouping.
 *
 * `minShorter` is the original's rarely used floor: abbreviation only kicks in
 * above `10 ** minShorter`. The dashboard passes 0, i.e. anything above 1.
 */
export function legacyShortPrice(
  value: number,
  t: (key: string) => string,
  minShorter = 0,
): string {
  const isNegative = value < 0;
  let n = Math.abs(value);
  let selected = '';

  for (let i = ABBREV.length - 1; i >= 0; i--) {
    const size = 10 ** ((i + 1) * 3);
    const minSize = 10 ** minShorter;

    if (size <= n && minSize < n) {
      const raw = n / size;

      // Decimal count comes from the raw quotient's own precision, trimmed of
      // trailing zeros — never a fixed width.
      const [, decimal = ''] = raw.toString().split('.');
      let decimals = Math.min(decimal.length, MAX_DECIMALS);
      while (decimals > 1 && decimal[decimals - 1] === '0') decimals--;

      const factor = 10 ** decimals;
      n = Math.round(raw * factor) / factor;

      // Rounding up to a full 1000 promotes to the next unit.
      if (n === 1000 && i < ABBREV.length - 1) {
        n = 1;
        i++;
      }

      selected = ABBREV[i] ?? '';
      break;
    }
  }

  if (!selected) {
    return isNegative ? `-${formatThousandsComma(n)}` : formatThousandsComma(n);
  }

  const actualDecimals = n % 1 === 0 ? 0 : (n.toString().split('.')[1]?.length ?? 0);
  const formatted = n.toFixed(Math.min(actualDecimals, MAX_DECIMALS)).replace('.', ',');

  return `${isNegative ? '-' : ''}${formatted} ${t(selected)}`;
}
